import type { NextRequest } from "next/server";

/**
 * Canonical site URL for OAuth / email redirects.
 * Set `NEXT_PUBLIC_SITE_URL` in production (e.g. `https://www.getsmartresume.com`) and add the same
 * origin + `/auth/callback` in Supabase → Authentication → URL Configuration → Redirect URLs.
 */
function tryParseOrigin(raw0: string | undefined): string | null {
  const raw = (raw0 ?? "").trim().replace(/\/$/, "");
  if (!raw || !/^https?:\/\//i.test(raw)) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

/**
 * Server-only: prefers `NEXT_PUBLIC_SITE_URL`, then `SITE_URL` (Vercel can set without rebuilding client).
 */
export function publicSiteOriginFromEnvServer(): string | null {
  return tryParseOrigin(process.env.NEXT_PUBLIC_SITE_URL) ?? tryParseOrigin(process.env.SITE_URL);
}

/**
 * Client bundle: only `NEXT_PUBLIC_SITE_URL` is available at build time.
 */
export function publicSiteOriginFromEnv(): string | null {
  return tryParseOrigin(process.env.NEXT_PUBLIC_SITE_URL);
}

/** Client: OAuth `redirectTo` must match Supabase allow list; env wins over `window.location.origin`. */
export function getPublicSiteOriginClient(): string {
  const fromEnv = publicSiteOriginFromEnv();
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return "";
}

/**
 * Server: where to send the browser after `/auth/callback` exchanges the code.
 * Prefer env, then proxy headers (Vercel), then `request.nextUrl.origin`.
 */
export function getPublicSiteOriginFromRequest(request: Pick<NextRequest, "nextUrl" | "headers">): string {
  const fromEnv = publicSiteOriginFromEnvServer();
  if (fromEnv) return fromEnv;

  const host = request.headers.get("x-forwarded-host")?.split(",")[0]?.trim();
  const xfProto = request.headers.get("x-forwarded-proto")?.split(",")[0]?.trim();
  if (host && !host.includes("localhost") && !host.startsWith("127.0.0.1")) {
    const proto =
      xfProto === "https" || xfProto === "http"
        ? xfProto
        : request.nextUrl.protocol === "https:"
          ? "https"
          : "http";
    return `${proto}://${host}`;
  }
  return request.nextUrl.origin;
}
